import { Typography, Button, Alert, Grid, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	OtherReferenceInterviewProvider,
	useOtherReferenceInterviewWizard,
} from './useOtherReferenceInterviewWizard';

import { RecommendationComponent } from '../RecommendationComponent';
import {
	OtherReferenceFeedback,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallback, useEffect, useState } from 'react';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { useAuth } from '../../../hooks/useAuth';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="flex-grow text-primary-blue text-lg font-bold py-5">
				{heading}
			</Text>
			<Text className="mb-[8px]">{description}</Text>
		</div>
	);
};

export const OtherReferenceProposerRecommendationContainer = () => {
	const { t } = useTranslation();
	const {
		onSubmitClick,
		application,
		otherReferencePreceptorRecommendation,
		setOtherReferencePreceptorRecommendation,
		otherReferenceRecord,
		setOtherReferenceRecord,
	} = useOtherReferenceInterviewWizard();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const [form] = Form.useForm<OtherReferenceFeedback>();
	const formWatchvalues = Form.useWatch<OtherReferenceFeedback>([], form);
	const { navigateWithApplicationId } = useApplicationId();
	const [isFormValid, setIsFormValid] = useState(false);
	const { role } = useAuth();
	const isAdminOrApprover =
		role === UserRole.APPROVER || role === UserRole.ADMIN;

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setOtherReferencePreceptorRecommendation(fieldValues);
			setOtherReferenceRecord(fieldValues);
			setIsFormValid(true);
		} catch (e) {
			setIsFormValid(false);
		}
	}, [form, setOtherReferencePreceptorRecommendation, setOtherReferenceRecord]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, formWatchvalues]);

	useEffect(() => {
		form.setFieldsValue({
			...otherReferencePreceptorRecommendation,
			referenceRecord: otherReferenceRecord?.referenceRecord as string,
		});
	}, [form, otherReferencePreceptorRecommendation, otherReferenceRecord]);

	const handleSubmit = useCallback(async () => {
		try {
			const values = await form.validateFields();
			onSubmitClick(values);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Validation failed:', error);
		}
	}, [form, onSubmitClick]);

	const handleCancelClick = useCallback(() => {
		navigateWithApplicationId(INTERVIEW_LANDING);
	}, [navigateWithApplicationId]);
	return (
		<div
			className={`flex flex-col h-full min-h-0 bg-white mx-auto rounded-lg mt-4 border border-solid border-[#E6E6E6] ${
				isMobile ? 'w-[95%] max-w-[90%] p-4' : 'w-3/4 max-w-[50%] p-8'
			}`}
		>
			<SectionHeader
				heading={t('collectOtherReferenceRecommendation')}
				description={t('askOtherReferenceForHisOverallRecommendation')}
			/>

			<Text className="pb-4 font-bold">
				{'1. '}
				{t('howLongHaveYouKnownTheApplicant')}
			</Text>
			<Form form={form}>
				<Form.Item
					name="referenceRecord"
					rules={[
						{ required: true, message: <span>{t('required')}</span> },
						{ min: 10, message: <span>{t('commentsShouldBeClear')}</span> },
					]}
				>
					<Input.TextArea
						showCount
						rows={4}
						className="font-sans text-sm font-normal text-[#919191]"
						disabled={isAdminOrApprover}
					/>
				</Form.Item>

				<Text className="font-bold text-lg text-primary-blue text-center">
					{t('proposersFeedbackForCategory', {
						categoryTitle: t('otherReference'),
					})}
				</Text>

				<RecommendationComponent
					form={form}
					displayComments={false}
					hideReasons
					disabled={isAdminOrApprover}
				/>

				<div className="flex flex-row p-2 justify-center">
					<Button onClick={() => void handleCancelClick()}>
						{t('cancel')}
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						disabled={!isFormValid || isAdminOrApprover}
						className="ml-4"
						onClick={() => void handleSubmit()}
					>
						{t('saveAndContinue')}
					</Button>
				</div>
			</Form>

			{application?.isApprover && (
				<Alert
					showIcon
					message={t('onlyProposersAreAllowedToSubmitFeedback')}
					type="warning"
				/>
			)}
		</div>
	);
};

export const OtherReferenceProposerRecommendationPage = () => {
	return (
		<OtherReferenceInterviewProvider>
			<OtherReferenceProposerRecommendationContainer />
		</OtherReferenceInterviewProvider>
	);
};
